import { getRouter } from '../../../../shared/lib/comm/gateway_ws/connection';
import { TokenManager } from '../../../../shared/lib/apiClient';
import { getFromStorage } from '../../../../shared/lib/storageUtils';
import { readGithubSettingsWithServerFallback } from './discoveryGithubSettingsGate';
import type { DockerBuildContract } from '../build_feature/types';

const BUILD_FEATURE_CHANNEL = 'build_feature' as const;

/**
 * Same settings merge as useVishCoderActions.generateBuildContract (appSettings + project git).
 */
async function buildMergedSettings(projectId: string | number): Promise<Record<string, unknown>> {
    let parsedSettings: Record<string, unknown> = {};
    try {
        const savedSettings = getFromStorage<Record<string, unknown>>('appSettings');
        if (savedSettings) parsedSettings = { ...savedSettings };
        const githubSettings = await readGithubSettingsWithServerFallback(projectId);
        if (githubSettings) {
            parsedSettings = {
                ...parsedSettings,
                github: {
                    repo_url: githubSettings.repo_url,
                    branch: githubSettings.branch,
                    token: githubSettings.token,
                },
            };
        }
    } catch {
        /* ignore */
    }
    return parsedSettings;
}

/**
 * Feature-shaped contract for discovery proceed: same top-level keys as build flow, empty requirement objects.
 */
export async function buildDiscoveryProceedContract(projectId: string | number): Promise<DockerBuildContract> {
    const projectIdStr = String(projectId);
    const featureNumber = `req_discovery_${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`;
    const userId = TokenManager.getUserId();

    return {
        contractKind: 'feature',
        metadata: {
            initiatedBy: 'user',
            dateTime: new Date().toISOString(),
            feature_Number: featureNumber,
            user_id: userId,
            project_id: projectIdStr,
        },
        settings: await buildMergedSettings(projectId),
        requirements: {
            high_level_requirements: {},
            low_level_requirements: {},
            test_cases: [],
            user_flow: {},
        },
    };
}

/**
 * Same wire path as ContractPopupModal "Send to VishCoder": build_feature, streaming, contract.
 */
export async function sendDiscoveryProceedContractToVishCoder(projectId: string | number): Promise<boolean> {
    const router = getRouter();
    const comm = router.getCommunicationManager();
    if (!comm) return false;

    const c = await buildDiscoveryProceedContract(projectId);

    const threadId =
        (c.requirements.low_level_requirements as { uiid?: string } | undefined)?.uiid ||
        c.metadata.feature_Number ||
        c.metadata.dateTime ||
        `contract_${Date.now()}`;

    const userId = TokenManager.getUserId() || c.metadata.user_id;
    const projectIdStr = c.metadata.project_id || String(projectId);

    const messageId = crypto.randomUUID();
    const timestamp = c.metadata.dateTime;

    router.setChannelContext(
        BUILD_FEATURE_CHANNEL,
        {
            threadId: String(threadId),
            originMessageId: messageId,
            originActor: 'user',
            origints: timestamp,
            ...(userId && { userId: String(userId) }),
            ...(projectIdStr && { projectId: String(projectIdStr) }),
            ...(threadId && { featureId: String(threadId) }),
        },
        messageId,
        'user',
        timestamp
    );

    const contractPayload = {
        contract: {
            metadata: c.metadata,
            settings: c.settings,
            requirements: c.requirements,
        },
    };

    return comm.emit(BUILD_FEATURE_CHANNEL, 'user', 'streaming', 'contract', contractPayload, 'request');
}
